/**
 * Utilidades y Helpers para Styled-Components
 * 
 * Este archivo contiene funciones helper que facilitan
 * el trabajo con styled-components en el proyecto
 */

import { css } from 'styled-components';

/**
 * Helper para media queries responsive
 * 
 * Uso:
 * const Component = styled.div`
 *   font-size: 16px;
 *   ${media.md`font-size: 18px;`}
 *   ${media.lg`font-size: 20px;`}
 * `;
 */
export const media = {
    // Mobile first (min-width)
    sm: (...args) => css`
        @media (min-width: ${props => props.theme.breakpoints.sm}) {
            ${css(...args)}
        }
    `,
    md: (...args) => css`
        @media (min-width: ${props => props.theme.breakpoints.md}) {
            ${css(...args)}
        }
    `,
    lg: (...args) => css`
        @media (min-width: ${props => props.theme.breakpoints.lg}) {
            ${css(...args)}
        }
    `,
    xl: (...args) => css`
        @media (min-width: ${props => props.theme.breakpoints.xl}) {
            ${css(...args)}
        }
    `,
    xxl: (...args) => css`
        @media (min-width: ${props => props.theme.breakpoints.xxl}) {
            ${css(...args)}
        }
    `,
    
    // Max-width (para casos específicos)
    maxSm: (...args) => css`
        @media (max-width: ${props => props.theme.breakpoints.sm}) {
            ${css(...args)}
        }
    `,
    maxMd: (...args) => css`
        @media (max-width: ${props => props.theme.breakpoints.md}) {
            ${css(...args)}
        }
    `,
    maxLg: (...args) => css`
        @media (max-width: ${props => props.theme.breakpoints.lg}) {
            ${css(...args)}
        }
    `,
    
    // Rango específico
    between: (min, max) => (...args) => css`
        @media (min-width: ${min}) and (max-width: ${max}) {
            ${css(...args)}
        }
    `,
};

/**
 * Estilos de variantes comunes para botones
 */
export const variantStyles = {
    primary: css`
        background-color: ${props => props.theme.colors.primary};
        color: #fff;
        border: 1px solid ${props => props.theme.colors.primary};
        
        &:hover {
            opacity: 0.9;
        }
    `,
    secondary: css`
        background-color: ${props => props.theme.colors.secondary};
        color: #fff;
        border: 1px solid ${props => props.theme.colors.secondary};
        
        &:hover {
            opacity: 0.9;
        }
    `,
    danger: css`
        background-color: ${props => props.theme.colors.button.danger};
        color: #fff;
        border: 1px solid ${props => props.theme.colors.button.danger};
        
        &:hover {
            opacity: 0.9;
        }
    `,
    success: css`
        background-color: ${props => props.theme.colors.success};
        color: #fff;
        border: 1px solid ${props => props.theme.colors.success};
        
        &:hover {
            opacity: 0.9;
        }
    `,
    text: css`
        background-color: transparent;
        color: ${props => props.theme.colors.primary};
        border: none;
        
        &:hover {
            background-color: ${props => props.theme.colors.background.hover};
        }
    `,
    icon: css`
        background-color: ${props => props.theme.colors.button.icon};
        border: 1px solid ${props => props.theme.colors.button.icon};
        color: ${props => props.theme.colors.primary};
        
        &:hover {
            background-color: ${props => props.theme.colors.primary};
            border-color: ${props => props.theme.colors.primary};
            color: #fff;
        }
    `,
};

/**
 * Estilos de tamaños para componentes
 */
export const sizeStyles = {
    small: css`
        padding: 5px 10px;
        font-size: ${props => props.theme.typography.fontSize.xs};
        height: ${props => props.theme.heights.button.small};
    `,
    medium: css`
        padding: 8px 15px;
        font-size: ${props => props.theme.typography.fontSize.sm};
        height: ${props => props.theme.heights.button.default};
    `,
    large: css`
        padding: 10px 20px;
        font-size: ${props => props.theme.typography.fontSize.md};
        height: 46px;
    `,
};

/**
 * Helper para obtener valores del theme de forma segura
 * 
 * Uso:
 * const color = getThemeValue('colors.primary')(props);
 */
export const getThemeValue = (path) => (props) => {
    const keys = path.split('.');
    let value = props.theme;
    
    for (const key of keys) {
        if (value && typeof value === 'object') {
            value = value[key];
        } else {
            return undefined;
        }
    }
    
    return value;
};

/**
 * Helper para aplicar estilos condicionales
 * 
 * Uso:
 * ${ifProp('disabled', css`opacity: 0.6;`, css`opacity: 1;`)}
 */
export const ifProp = (prop, trueValue, falseValue = '') => (props) => {
    return props[prop] ? trueValue : falseValue;
};

/**
 * Helper para aplicar estilos cuando una prop tiene un valor específico
 * 
 * Uso:
 * ${whenProp('variant', 'primary', css`background: blue;`)}
 */
export const whenProp = (prop, value, styles) => (props) => {
    return props[prop] === value ? styles : '';
};

/**
 * Helper para aplicar múltiples variantes
 * 
 * Uso:
 * ${applyVariants('variant', variantStyles)}
 */
export const applyVariants = (prop, variants) => (props) => {
    const variant = props[prop];
    return variant && variants[variant] ? variants[variant] : '';
};

/**
 * Helper para truncar texto con ellipsis
 */
export const truncate = css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/**
 * Helper para multi-line truncate
 */
export const truncateLines = (lines = 2) => css`
    display: -webkit-box;
    -webkit-line-clamp: ${lines};
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

/**
 * Helper para centrar contenido
 */
export const centerContent = css`
    display: flex;
    align-items: center;
    justify-content: center;
`;

/**
 * Helper para flexbox común
 */
export const flex = {
    row: css`
        display: flex;
        flex-direction: row;
    `,
    column: css`
        display: flex;
        flex-direction: column;
    `,
    center: css`
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    between: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    around: css`
        display: flex;
        align-items: center;
        justify-content: space-around;
    `,
};

/**
 * Helper para posicionamiento absoluto centrado
 */
export const absoluteCenter = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

/**
 * Helper para scrollbar personalizado (Webkit)
 */
export const customScrollbar = css`
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: ${props => props.theme.colors.background.default};
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${props => props.theme.colors.border.default};
        border-radius: 4px;
        
        &:hover {
            background: ${props => props.theme.colors.border.dark};
        }
    }
`;

export default {
    media,
    variantStyles,
    sizeStyles,
    getThemeValue,
    ifProp,
    whenProp,
    applyVariants,
    truncate,
    truncateLines,
    centerContent,
    flex,
    absoluteCenter,
    customScrollbar,
};

